import { useState } from 'preact/hooks';
import { AlertCircleIcon, SendIcon } from './Icons.jsx';

/**
 * Developer diagnostics, rendered only on localhost or when `testTools` is set
 * explicitly. It never appears in a real merchant's admin.
 *
 * The two notify buttons broadcast to *every agent* on this app — socket for
 * whoever has the dashboard open, Web Push for every registered agent device.
 * Neither depends on this visitor having granted notification permission, so
 * they work straight away.
 *
 * "Tone" stays local on purpose: it isolates the WebAudio path, so a silent
 * alert can be told apart from an undelivered one.
 */
export function TestTools({ onNotifyAgents, onTestTone }) {
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const notify = async (isEmergency) => {
    setBusy(true);
    setResult(null);
    try {
      const outcome = await onNotifyAgents(isEmergency);
      const live = outcome.socketTargets;
      const pushed = outcome.push?.sent ?? 0;

      const tabs = outcome.socketConnections ?? live;
      const parts = [];
      if (live > 0) {
        // Distinguish people from tabs — one agent with three windows open is
        // still one agent, and saying "3" would overstate the reach.
        parts.push(
          `${live} agent${live === 1 ? '' : 's'} live${tabs > live ? ` (${tabs} tabs)` : ''}`
        );
      }
      if (pushed > 0) parts.push(`${pushed} push device${pushed === 1 ? '' : 's'}`);

      if (parts.length > 0) {
        setResult(`Delivered to ${parts.join(' + ')}.`);
      } else if (!outcome.pushEnabled) {
        setResult('No agent online, and web push is not configured on the server.');
      } else {
        setResult('No agent online and no agent device registered for push. Nothing delivered.');
      }
    } catch (err) {
      setResult(err.message ?? 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="testtools">
      <div class="testtools__label">DIAGNOSTICS · NOTIFY ALL AGENTS</div>

      <div class="testtools__row">
        <button
          type="button"
          class="testtools__button testtools__button--primary"
          onClick={() => notify(false)}
          disabled={busy}
        >
          <SendIcon size={11} color="currentColor" />
          Notify agents
        </button>

        <button
          type="button"
          class="testtools__button testtools__button--danger"
          onClick={() => notify(true)}
          disabled={busy}
        >
          <AlertCircleIcon size={11} />
          Emergency
        </button>

        <button type="button" class="testtools__button" onClick={onTestTone} disabled={busy}>
          Tone
        </button>
      </div>

      {result && <div class="testtools__note">{result}</div>}
    </div>
  );
}

export default TestTools;
